<template>
  <div class="categories-tab pa-6">
    <v-card class="content-card elevation-0">
      <v-card-title class="pb-4">
        <div class="d-flex align-center justify-space-between" style="width: 100%;">
          <div style="flex: 1;">
            <div class="card-title">Kategorije za Orderman</div>
            <div class="card-subtitle">
              Odaberite kategorije, poredajte ih i dodajte alternativne nazive
            </div>
            <div class="text-caption mt-2" style="color: #9e9e9e;">
              <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
              Sve kategorije iz sustava • Označite aktivne • Promijenite redoslijed • Dodajte alternativne nazive
            </div>
          </div>
          <div class="d-flex align-center">
            <v-btn
              color="secondary"
              class="soft-button mr-2"
              outlined
              @click="toggleSelectAll"
            >
              <v-icon left small>
                {{ allSelected ? 'mdi-checkbox-multiple-marked' : 'mdi-checkbox-multiple-blank-outline' }}
              </v-icon>
              {{ allSelected ? 'Odznači sve' : 'Označi sve' }}
            </v-btn>
            <v-btn
              color="primary"
              class="soft-button"
              :loading="saving"
              :disabled="saving"
              @click="saveCategories"
            >
              <v-icon left small>mdi-content-save</v-icon>
              Spremi
            </v-btn>
          </div>
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Categories list -->
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          />
          <div class="mt-4 text--secondary">Učitavanje kategorija...</div>
        </div>

        <div v-else-if="categories.length > 0">
          <v-list class="categories-list transparent">
            <draggable
              v-model="sortedCategories"
              handle=".drag-handle"
              @end="onDragEnd"
            >
              <v-list-item
                v-for="(category, index) in sortedCategories"
                :key="category.id"
                class="category-item-compact mb-2"
              >
                <div class="category-row">
                  <!-- Drag handle -->
                  <v-icon class="drag-handle mr-2" style="cursor: move;">
                    mdi-drag-vertical
                  </v-icon>

                  <!-- Checkbox -->
                  <v-checkbox
                    v-model="category.selected"
                    color="primary"
                    hide-details
                    class="mt-0 mr-3"
                  />

                  <!-- Order badge -->
                  <v-chip
                    small
                    color="primary"
                    outlined
                    class="order-chip mr-3"
                  >
                    #{{ index + 1 }}
                  </v-chip>

                  <!-- Category name -->
                  <div class="category-name-text mr-3">
                    {{ category.name }}
                    <v-chip
                      v-if="category.alternativeName && category.alternativeName.trim()"
                      x-small
                      color="info"
                      text-color="white"
                      class="ml-2"
                    >
                      <v-icon x-small left>mdi-pencil</v-icon>
                      Preimenovano
                    </v-chip>
                  </div>

                  <!-- Alternative name input -->
                  <v-text-field
                    v-model="category.alternativeName"
                    label="Alternativni naziv"
                    placeholder="Opcionalno"
                    dense
                    outlined
                    hide-details
                    class="alternative-name-input-compact mr-3"
                  >
                    <template v-slot:append>
                      <v-icon
                        v-if="category.alternativeName && category.alternativeName.trim()"
                        small
                        color="success"
                      >
                        mdi-check-circle
                      </v-icon>
                    </template>
                  </v-text-field>

                  <!-- Reorder buttons -->
                  <div class="d-flex align-center" style="gap: 4px;">
                    <v-btn
                      class="arrow-button"
                      :disabled="index === 0"
                      @click="moveUp(index)"
                    >
                      <v-icon>mdi-chevron-up</v-icon>
                    </v-btn>
                    <v-btn
                      class="arrow-button"
                      :disabled="index === sortedCategories.length - 1"
                      @click="moveDown(index)"
                    >
                      <v-icon>mdi-chevron-down</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-list-item>
            </draggable>
          </v-list>

          <!-- Summary -->
          <v-alert
            v-if="selectedCount > 0"
            dense
            text
            color="info"
            class="mt-4"
          >
            <v-icon left small>mdi-information</v-icon>
            Odabrano {{ selectedCount }} od {{ sortedCategories.length }} kategorija
          </v-alert>
        </div>

        <!-- Empty state -->
        <div v-else class="text-center py-8 empty-state">
          <v-icon size="64" color="grey lighten-2">mdi-shape-outline</v-icon>
          <div class="text-h6 mt-4 text--secondary">Nema kategorija</div>
          <div class="text-caption text--secondary">
            Dodajte kategorije u sustav kako bi se prikazale ovdje
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { df } from '@/plugins/firebase'
import draggable from 'vuedraggable'

export default {
  name: 'CategoriesTab',

  components: {
    draggable
  },

  props: {
    locationId: {
      type: String,
      required: true
    },
    companyId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      loading: false,
      saving: false,
      categories: [],
      ordermanCategoriesMap: {} // Map of categoryId -> orderman data
    }
  },

  computed: {
    sortedCategories: {
      get () {
        return this.categories
      },
      set (value) {
        this.categories = value
      }
    },

    selectedCount () {
      return this.categories.filter(c => c.selected).length
    },

    allSelected () {
      return this.categories.length > 0 && this.categories.every(c => c.selected)
    }
  },

  created () {
    this.loadData()
  },

  watch: {
    locationId () {
      this.loadData()
    }
  },

  methods: {
    async loadData () {
      this.loading = true

      try {
        // Load company categories and orderman categories in parallel
        await Promise.all([
          this.loadCompanyCategories(),
          this.loadOrdermanCategories()
        ])

        // Merge and sort categories
        this.mergeCategoriesData()
      } catch (error) {
        this.$root.$emit('show-snackbar', {
          text: 'Greška pri učitavanju podataka',
          color: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    async loadCompanyCategories () {
      const doc = await df
        .collection('company_categories')
        .doc(this.companyId)
        .get()

      if (!doc.exists) {
        this.categories = []
        return
      }

      const data = doc.data()

      if (!data.categories || typeof data.categories !== 'object') {
        this.categories = []
        return
      }

      // Convert categories object to array
      this.categories = Object.keys(data.categories)
        .map(key => {
          const cat = data.categories[key]
          return {
            id: cat.id || key,
            name: cat.name || '',
            image: cat.picture && cat.picture.trim() !== '' ? cat.picture : null,
            status: cat.status || '',
            selected: false,
            order: 9999,
            alternativeName: ''
          }
        })
        .filter(cat => cat.status === 'OK') // Only show active categories
    },

    async loadOrdermanCategories () {
      try {
        const snapshot = await df
          .collection('locations')
          .doc(this.locationId)
          .collection('orderman_categories')
          .get()

        // Create map for quick lookup
        this.ordermanCategoriesMap = {}
        snapshot.docs.forEach(doc => {
          const data = doc.data()
          this.ordermanCategoriesMap[doc.id] = {
            active: data.active || false,
            order: data.order || 9999,
            alternativeName: data.name || ''
          }
        })
      } catch (error) {
        this.ordermanCategoriesMap = {}
      }
    },

    mergeCategoriesData () {
      // Get all company categories
      const allCategories = []

      // Merge with orderman data from subcollection
      this.categories.forEach(cat => {
        const ordermanData = this.ordermanCategoriesMap[cat.id]

        if (ordermanData) {
          cat.selected = ordermanData.active
          cat.order = ordermanData.order
          cat.alternativeName = ordermanData.alternativeName
        } else {
          cat.selected = false
          cat.order = 9999
          cat.alternativeName = ''
        }
        allCategories.push(cat)
      })

      // Sort by order (selected first, then by order number)
      allCategories.sort((a, b) => {
        if (a.selected && !b.selected) return -1
        if (!a.selected && b.selected) return 1
        return a.order - b.order
      })

      this.categories = allCategories
    },

    async saveCategories () {
      this.saving = true

      try {
        const batch = df.batch()
        const subcollectionRef = df
          .collection('locations')
          .doc(this.locationId)
          .collection('orderman_categories')

        // Process all categories
        this.categories.forEach((cat, index) => {
          const categoryDocRef = subcollectionRef.doc(cat.id)

          if (cat.selected) {
            // Save selected category
            const data = {
              active: true,
              order: index + 1 // Order based on current position (1-indexed)
            }

            // Only include name if alternative name is provided
            if (cat.alternativeName && cat.alternativeName.trim() !== '') {
              data.name = cat.alternativeName.trim()
            }

            batch.set(categoryDocRef, data)
          } else {
            // Set unselected category as inactive
            batch.set(categoryDocRef, { active: false })
          }
        })

        // Commit all changes
        await batch.commit()

        this.$root.$emit('show-snackbar', {
          text: 'Kategorije uspješno ažurirane',
          color: 'success'
        })

        // Reload to verify
        await this.loadOrdermanCategories()
        this.mergeCategoriesData()
      } catch (error) {
        this.$root.$emit('show-snackbar', {
          text: 'Greška pri spremanju kategorija',
          color: 'error'
        })
      } finally {
        this.saving = false
      }
    },

    moveUp (index) {
      if (index === 0) return

      const item = this.categories[index]
      this.categories.splice(index, 1)
      this.categories.splice(index - 1, 0, item)
    },

    moveDown (index) {
      if (index === this.categories.length - 1) return

      const item = this.categories[index]
      this.categories.splice(index, 1)
      this.categories.splice(index + 1, 0, item)
    },

    onDragEnd () {
      // Drag ended, order updated
    },

    toggleSelectAll () {
      const newValue = !this.allSelected

      this.categories.forEach(cat => {
        cat.selected = newValue
      })

      this.$root.$emit('show-snackbar', {
        text: newValue ? 'Sve kategorije označene' : 'Sve kategorije odznačene',
        color: 'info'
      })
    }
  }
}
</script>

<style scoped>
.categories-tab {
  min-height: 400px;
}

.content-card {
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(0, 179, 190, 0.1) !important;
}

.card-title {
  font-size: 18px !important;
  font-weight: 700 !important;
  color: #2c3e50 !important;
  letter-spacing: 0.3px !important;
}

.card-subtitle {
  font-size: 14px !important;
  color: #6c757d !important;
  margin-top: 4px;
}

.soft-button {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border-radius: 12px !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  letter-spacing: 0.3px !important;
  padding: 10px 24px !important;
  height: 40px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.soft-button:hover {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
  transform: translateY(-1px);
}

.categories-list {
  background: transparent !important;
}

.category-item-compact {
  background: rgba(255, 255, 255, 0.9) !important;
  border-radius: 8px !important;
  border: 1px solid rgba(57, 137, 201, 0.1) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
  padding: 8px 12px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.category-item-compact:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1) !important;
  background: rgba(255, 255, 255, 0.95) !important;
}

.category-row {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}

.drag-handle {
  color: #9e9e9e !important;
  cursor: move !important;
  flex-shrink: 0;
}

.drag-handle:hover {
  color: #3989C9 !important;
}

.order-chip {
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  flex-shrink: 0;
  min-width: 50px;
}

.category-name-text {
  font-size: 15px !important;
  font-weight: 600 !important;
  color: #2c3e50 !important;
  min-width: 200px;
  flex-shrink: 0;
}

.alternative-name-input-compact {
  flex: 1;
  max-width: 350px;
}

.alternative-name-input-compact >>> .v-input__control {
  background: rgba(255, 255, 255, 0.95) !important;
  border-radius: 8px !important;
}

.alternative-name-input-compact >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-width: 1px !important;
}

.alternative-name-input-compact >>> .v-text-field__slot input {
  font-size: 13px !important;
}

.arrow-button {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border-radius: 10px !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.12) !important;
  backdrop-filter: blur(10px) !important;
  min-width: 36px !important;
  width: 36px !important;
  height: 36px !important;
  padding: 0 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.arrow-button:hover:not(.v-btn--disabled) {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 1) !important;
}

.arrow-button.v-btn--disabled {
  background: rgba(245, 245, 245, 0.6) !important;
  color: rgba(158, 158, 158, 0.5) !important;
  border-color: rgba(158, 158, 158, 0.1) !important;
  box-shadow: none !important;
  opacity: 0.6 !important;
}

.arrow-button .v-icon {
  font-size: 22px !important;
  color: inherit !important;
}

.empty-state {
  background: rgba(255, 255, 255, 0.5) !important;
  border-radius: 12px !important;
  border: 2px dashed rgba(57, 137, 201, 0.2) !important;
}

@media (max-width: 960px) {
  .categories-tab {
    padding: 16px;
  }
}
</style>
